import React, { useState } from "react";
import {
    SovosBox,
    SovosDivider,
    SovosIconButton,
    SovosPopover,
    SovosTextField,
    SovosTypography,
} from "mosaic-react";
import {
    KeyboardArrowDown,
    Search,
    Undo,
} from "mosaic-react-icons";
import { DO_NOT_IMPORT } from "../constants";
import FieldTypeIcon from "./FieldTypeIcon";

const DropdownItem = ({ label, type, recommended, disabled, selected, onClick }) => (
    <SovosBox
        component="button"
        onClick={onClick}
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            px: 1.5,
            py: 0.875,
            border: "none",
            bgcolor: selected ? "primary.50" : "transparent",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.4 : 1,
            fontFamily: "inherit",
            "&:hover": !disabled ? { bgcolor: selected ? "primary.50" : "action.hover" } : {},
        }}
    >
        {type ? (
            <FieldTypeIcon type={type} size={14} />
        ) : (
            <SovosBox sx={{ width: 14 }} />
        )}
        <SovosTypography
            variant="caption"
            color={selected ? "primary.main" : "text.primary"}
            fontWeight={selected ? 600 : 400}
            sx={{ flex: 1, textAlign: "left" }}
            noWrap
        >
            {label}
        </SovosTypography>
        {recommended && (
            <SovosBox
                sx={{
                    fontSize: 10,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    bgcolor: "success.light",
                    color: "success.dark",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                }}
            >
                Recommended
            </SovosBox>
        )}
    </SovosBox>
);

const MappingDropdown = ({ csvColumn, displayName, value, originalValue, mschFields, usedFields, onChange, onRevert, i18n }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState("");

    const open = Boolean(anchorEl);

    const selectedField = value === DO_NOT_IMPORT
        ? { name: "Do not import", type: null }
        : mschFields.find((f) => f.id === value);

    const matchName = displayName || csvColumn;
    const recommendedFields = mschFields.filter((f) => f.recommended?.includes(matchName));
    const filteredRecommended = recommendedFields.filter(
        (f) => search === "" || f.name.toLowerCase().includes(search.toLowerCase())
    );
    const otherFields = mschFields.filter(
        (f) => !f.recommended?.includes(csvColumn) &&
            (search === "" || f.name.toLowerCase().includes(search.toLowerCase()))
    );

    const handleOpen = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const handleClose = () => { setAnchorEl(null); setSearch(""); };

    const handleSelect = (fieldId) => {
        onChange(csvColumn, fieldId);
        handleClose();
    };

    const isUsed = (fieldId) => usedFields.includes(fieldId) && value !== fieldId;

    const isMapped = !!value;
    const isDoNotImport = value === DO_NOT_IMPORT;
    const buttonLabel = selectedField ? selectedField.name : "Map to";
    const isDirty = value !== originalValue;

    return (
        <>
            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                <SovosBox
                    component="button"
                    onClick={handleOpen}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: isMapped && !isDoNotImport ? "primary.main" : "divider",
                        bgcolor: isMapped && !isDoNotImport ? "primary.50" : "background.paper",
                        cursor: "pointer",
                        flex: 1,
                        minWidth: 0,
                        fontFamily: "inherit",
                        "&:hover": { borderColor: "primary.main" },
                    }}
                >
                    {isMapped && !isDoNotImport && selectedField && (
                        <FieldTypeIcon type={selectedField.type} size={13} />
                    )}
                    <SovosTypography
                        variant="caption"
                        color={isMapped && !isDoNotImport ? "primary.main" : "text.secondary"}
                        sx={{ flex: 1, textAlign: "left", fontWeight: isMapped ? 600 : 400 }}
                        noWrap
                        title={buttonLabel}
                    >
                        {buttonLabel}
                    </SovosTypography>
                    <KeyboardArrowDown sx={{ fontSize: 16, color: "text.secondary" }} />
                </SovosBox>
                {isDirty && (
                    <SovosIconButton
                        size="small"
                        tooltipText="Revert to original"
                        onClick={(e) => { e.stopPropagation(); onRevert(csvColumn); }}
                        sx={{ flexShrink: 0 }}
                    >
                        <Undo sx={{ fontSize: 14 }} />
                    </SovosIconButton>
                )}
            </SovosBox>

            <SovosPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { width: 260, overflow: "hidden" } }}
            >
                <SovosBox sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                    <SovosTextField
                        size="small"
                        fullWidth
                        placeholder="Search column name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <Search sx={{ fontSize: 16, mr: 0.5, color: "text.disabled" }} />,
                        }}
                        autoFocus
                    />
                </SovosBox>

                <SovosBox sx={{ maxHeight: 260, overflowY: "auto" }}>
                    <DropdownItem
                        label="Do not import"
                        type={null}
                        selected={value === DO_NOT_IMPORT}
                        onClick={() => handleSelect(DO_NOT_IMPORT)}
                    />
                    <SovosDivider />

                    {filteredRecommended.length > 0 && (
                        <>
                            {filteredRecommended.map((f) => (
                                <DropdownItem
                                    key={f.id}
                                    label={f.name}
                                    type={f.type}
                                    recommended
                                    disabled={isUsed(f.id)}
                                    selected={value === f.id}
                                    onClick={() => !isUsed(f.id) && handleSelect(f.id)}
                                />
                            ))}
                            {otherFields.length > 0 && <SovosDivider />}
                        </>
                    )}

                    {otherFields.map((f) => (
                        <DropdownItem
                            key={f.id}
                            label={f.name}
                            type={f.type}
                            disabled={isUsed(f.id)}
                            selected={value === f.id}
                            onClick={() => !isUsed(f.id) && handleSelect(f.id)}
                        />
                    ))}

                    {filteredRecommended.length === 0 && otherFields.length === 0 && (
                        <SovosBox sx={{ px: 2, py: 1.5 }}>
                            <SovosTypography variant="caption" color="text.disabled">
                                {i18n`No fields match`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                </SovosBox>
            </SovosPopover>
        </>
    );
};

export default MappingDropdown;
